// Push-Mitteilungen in der Ordaneo-App (docs/mobile/SPEZIFIKATION.md §12): das Gerät beim Server an- und abmelden.
//
//   enablePushOnDevice(client, who)    Erlaubnis anfragen (einmal), Token holen, beim Server anmelden, Marke setzen
//   disablePushOnDevice(client, token) beim Server und beim Mitteilungsdienst abmelden, Marke löschen
//   syncPushOnStart(client, who)       bei jedem Start der App: Hat DIESE Person in DIESEM Betrieb hier Mitteilungen
//                                      eingeschaltet, wird die Anmeldung aufgefrischt (neues Token, neue Sitzung). Sonst
//                                      wird ein Token dieses Geräts, das noch für jemand anderen angemeldet ist, abgemeldet
//                                      (geteiltes Gerät, Betriebswechsel) – ein Token gehört genau einer Mitgliedschaft.
//
// Die Marke (localStorage) merkt je Gerät, für wen Mitteilungen eingeschaltet sind und welches Token zuletzt gemeldet
// wurde. Sie ist Bequemlichkeit; maßgeblich ist der Server. Beim Abmelden entfernt der Server die Token der Sitzung von
// selbst – meldet sich dieselbe Person wieder an, schaltet der Start sie wieder ein.
import { profileRegisterPushDevice, profileUnregisterPushDevice } from '@modules/platform/contract'
import { nativeApp, nativePushPort, type NativePushPort } from '../lib/native'
import { readStored, writeStored } from '../lib/storage'
import type { ApiClient } from './client'

export interface PushWho {
  userId: string
  tenantId: string
}

interface PushMarker extends PushWho {
  token: string
}

const MARKER = 'push.device'
/** zuletzt freigegebenes Token je Person – damit nicht jeder Start erneut abmeldet */
const RELEASED = 'push.released'

const readMarker = (): PushMarker | null => {
  const value = readStored<PushMarker | null>(MARKER, null)
  return value &&
    typeof value.token === 'string' &&
    typeof value.userId === 'string' &&
    typeof value.tenantId === 'string'
    ? value
    : null
}

const isMine = (marker: PushMarker | null, who: PushWho): marker is PushMarker =>
  !!marker && marker.userId === who.userId && marker.tenantId === who.tenantId

/** Sind Mitteilungen auf diesem Gerät für diese Person (in diesem Betrieb) eingeschaltet? (laut Marke) */
export function pushEnabledHere(who: PushWho): boolean {
  return isMine(readMarker(), who)
}

export type EnablePushResult =
  { outcome: 'enabled'; deviceCount: number; token: string } | { outcome: 'denied' } | { outcome: 'unavailable' }

async function register(
  client: ApiClient,
  port: NativePushPort,
  who: PushWho,
  token: string,
  environment: 'sandbox' | 'production',
  previous: string | null,
): Promise<number> {
  const result = await client.run(profileRegisterPushDevice, {
    deviceToken: token,
    platform: port.platform,
    environment,
    ...(nativeApp()?.version ? { appVersion: nativeApp()?.version } : {}),
    ...(previous && previous !== token ? { previousDeviceToken: previous } : {}),
  })
  writeStored(MARKER, { token, userId: who.userId, tenantId: who.tenantId } satisfies PushMarker)
  writeStored(RELEASED, null)
  return result.deviceCount
}

/** „Mitteilungen auf diesem Gerät“ einschalten: fragt beim ersten Mal das System, meldet beim Server an. */
export async function enablePushOnDevice(client: ApiClient, who: PushWho): Promise<EnablePushResult> {
  const port = await nativePushPort()
  if (!port) return { outcome: 'unavailable' }
  const status = await port.status()
  if (!status.available) return { outcome: 'unavailable' }
  const registration = await port.register()
  if (registration.permission !== 'granted' || !registration.token) return { outcome: 'denied' }
  const marker = readMarker()
  const previous = isMine(marker, who) ? marker.token : (status.token ?? null)
  const deviceCount = await register(client, port, who, registration.token, registration.environment, previous)
  return { outcome: 'enabled', deviceCount, token: registration.token }
}

/** „Mitteilungen auf diesem Gerät“ ausschalten: beim Server abmelden (egal, für wen), dann beim Dienst. */
export async function disablePushOnDevice(client: ApiClient, token: string | null): Promise<void> {
  const port = await nativePushPort()
  const value = token ?? readMarker()?.token ?? (port ? (await port.status()).token : null)
  if (value) await client.run(profileUnregisterPushDevice, { deviceToken: value })
  writeStored(MARKER, null)
  // Der Dienst vergibt danach ein neues Token – eine spätere Anmeldung gilt dann einem frischen Gerät
  await port?.unregister().catch(() => undefined)
}

/**
 * Bei jedem Start der App (nach der Anmeldung). Fehler bleiben still – Mitteilungen sind Beiwerk, die Anwendung startet
 * immer. Liefert, was geschah (Tests).
 */
export async function syncPushOnStart(client: ApiClient, who: PushWho): Promise<'refreshed' | 'released' | 'none'> {
  try {
    const port = await nativePushPort()
    if (!port) return 'none'
    const status = await port.status()
    if (!status.available) return 'none'
    const marker = readMarker()
    const markedToken = marker?.token ?? null
    if (isMine(marker, who)) {
      if (status.permission !== 'granted') return 'none'
      const registration = await port.register()
      if (registration.permission !== 'granted' || !registration.token) return 'none'
      await register(client, port, who, registration.token, registration.environment, marker.token)
      return 'refreshed'
    }
    // Das Gerät hat ein Token, aber Mitteilungen sind hier nicht für diese Person eingeschaltet: Gehört es noch jemand
    // anderem (geteiltes Gerät) oder einem anderen Betrieb, wird es dort abgemeldet – einmal je Token und Person
    const token = status.token ?? markedToken
    if (!token) return 'none'
    const released = readStored<{ token?: string; userId?: string; tenantId?: string } | null>(RELEASED, null)
    if (released && released.token === token && released.userId === who.userId && released.tenantId === who.tenantId)
      return 'none'
    await client.run(profileUnregisterPushDevice, { deviceToken: token })
    writeStored(RELEASED, { token, userId: who.userId, tenantId: who.tenantId })
    return 'released'
  } catch {
    return 'none'
  }
}
